import React from "react";
import {
    ModuleRow as StyledModuleRow,
    ModuleLeft,
    ModuleIcon,
    ModuleName,
    ModuleRight,
    ModuleSemester,
    ModulePrice,
    ModuleActions,
    ActionButton,
    ActionButtonSpacer,
    ChevronButton
} from "../styles/primitives";
import StatusBadge from "./StatusBadge";
import { deriveModuleSource } from "../../shared/utils/moduleHelpers";
import { tokens } from "../../../../../public/tokens";

const ModuleRow = ({
    module,
    isFirst,
    isLast,
    isSelected,
    isEditMode,
    isExpanded,
    canExpand,
    shouldDisableAddButton,
    onAddModule,
    onRemoveModule,
    onToggleExpand
}) => {
    const moduleSource = module.moduleSource || deriveModuleSource(module);
    const statusText = String(module.status || "").trim().toLowerCase();
    const reasonText = String(module.reason || "").trim().toLowerCase();
    const normalizedStatus = (statusText || reasonText || "").toLowerCase();

    // ENROLLED takes precedence for selection/eligibility, but the badge can still show UPCOMING when module_status is "Inactive"
    const isEnrolled = statusText === "enrolled" || reasonText === "enrolled";
    const isInactiveForBadge = String(module.module_status || "").trim().toLowerCase() === "inactive";
    const isInactive = !isEnrolled && isInactiveForBadge;
    const isNotEligible = normalizedStatus === "deregister" || isInactive;
    const isCreditModule = moduleSource === "transfer";
    const pricePrefix = isCreditModule ? "-" : "";
    
    const isCore = module.type === "required" || module.type === "core" || module.type === "foundational" || module.is_core;

    const hasDropdown = isEditMode && canExpand;
    const shouldMuteRow = isEnrolled && !isEditMode;

    const handleRowClick = () => {
        if (hasDropdown && onToggleExpand) {
            onToggleExpand();
        }
    };

    return (
        <StyledModuleRow 
            $isFirst={isFirst} 
            $isLast={!isExpanded && isLast} 
            $isExpanded={isExpanded} 
            $hasDropdown={hasDropdown}
            $muted={shouldMuteRow}
            onClick={hasDropdown ? handleRowClick : undefined}
        >
            <ModuleLeft>
                <div style={{display: "inline-flex", alignItems: "center", gap: "5px"}}>
                <ModuleIcon>
                    {isCore ? (
                        <i
                            title="Core"
                            className="fa-light fa-circle-exclamation-check"
                            style={{ color: "var(--purple)" }}
                        />
                    ) : (
                        <i
                            title="Elective"
                            className="fa-light fa-arrow-progress"
                            style={{ color: "var(--teal)"}}
                        />
                    )}
                </ModuleIcon>
                <ModuleName $muted={shouldMuteRow}>{module.name}</ModuleName>
                </div>
          
                    <StatusBadge status={isInactiveForBadge ? "inactive" : module.status} />
               
            </ModuleLeft>
            <ModuleRight>
        <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "2px"}}>
                <ModuleSemester>
                    <i className="fa-light fa-clock" style={{ color: tokens.colors.theme["button-color"]}}></i>
                    <span>{module.term === "1, 2" ? "Year Module" : `Semester ${module.term || 1}`}</span>
                </ModuleSemester>
                <ModulePrice $isCredit={isCreditModule} $hidden={isEnrolled}>
                    {pricePrefix}
                    {new Intl.NumberFormat("en-ZA", {
                        style: "currency",
                        currency: "ZAR",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }).format(Number(module.price) || 0)}
                </ModulePrice>
                </div>
                <ModuleActions>
                    {isEditMode && (
                        (isNotEligible || isEnrolled) ? (
                            <ActionButtonSpacer />
                        ) : isSelected ? (
                            <ActionButton 
                                $variant="delete"
                                onClick={(e) => { e.stopPropagation(); onRemoveModule(module, moduleSource); }}
                                type="button"
                                title="Remove from selection"
                                aria-label="Remove from selection"
                            >
                                <i className="fa-light fa-trash-alt"></i>
                            </ActionButton>
                        ) : (
                            <ActionButton 
                                $variant="add"
                                $disabled={shouldDisableAddButton}
                                onClick={(e) => { e.stopPropagation(); onAddModule(module); }}
                                type="button"
                                title="Add to selection"
                                aria-label="Add to selection"
                                disabled={shouldDisableAddButton}
                            >
                                <i className="fal fa-plus"></i>
                            </ActionButton>
                        )
                    )}
                    {isEditMode && canExpand && (
                        <ChevronButton
                            onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
                            type="button"
                            aria-label={isExpanded ? "Collapse module details" : "Expand module details"}
                        >
                            <i className={`fa-light ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                        </ChevronButton>
                    )}
                </ModuleActions>
            </ModuleRight>
        </StyledModuleRow>
    );
};

export default ModuleRow;
